import { useMutation, useQueryClient } from '@tanstack/react-query';
import { userService } from '@services/user';
import { toast } from 'react-toastify';
import { useTranslation } from '@hooks/useTranslation';

interface UpdateOrganizationRoleParams {
  userId: string;
  role: string;
  /** Target tenant. Omit to fall back to the caller's active organization. */
  organizationId?: string;
}

/**
 * Change a user's role in a specific organization.
 *
 * Only writes the membership row, so unlike the Users page's employee edit it
 * works against tenants the caller is not currently acting in — which is how a
 * SUPER_ADMIN re-roles a user in any organization.
 */
export const useUpdateUserOrganizationRole = () => {
  const tActionMessages = useTranslation('actionMessages');
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['users', 'updateOrganizationRole'],
    mutationFn: async ({
      userId,
      role,
      organizationId,
    }: UpdateOrganizationRoleParams) => {
      return await userService.updateUserOrganizationRole(
        userId,
        role,
        organizationId,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      queryClient.invalidateQueries({ queryKey: ['activeSeats'] });
      toast.success(tActionMessages.roleUpdatedSuccessfully);
    },
    onError: (error: any) => {
      const message =
        error?.response?.errors?.[0]?.message ||
        error?.message ||
        'Failed to update role';
      toast.error(message);
    },
  });
};

export default useUpdateUserOrganizationRole;
